import React from 'react';
import { useAppDispatch, useAppSelector } from '@/store/store';
import { usePlayer } from '../PlayerProvider';
import { selectDjMode } from '../decksSlice';
import { selectLikedVideos, toggleVideoLike } from '@/features/videos/videosSlice';
import { LikeButton } from '@/components/likes';
import VinylRecord from './VinylRecord';
import ProgressBar from './ProgressBar';
import PlayerControls from './PlayerControls';
import TrackInfo from './TrackInfo';

interface GlobalPlayerProps {
  className?: string;
}

const GlobalPlayer: React.FC<GlobalPlayerProps> = ({ className = '' }) => {
  const { currentVideo } = usePlayer();
  const djMode = useAppSelector(selectDjMode);
  const likedVideos = useAppSelector(selectLikedVideos);
  const dispatch = useAppDispatch();

  if (!currentVideo || djMode) {
    return null;
  }
  
  return (
    <div className={`
      global-player fixed bottom-0 left-0 right-0 z-50
      bg-gradient-to-r from-meradio-dark via-neutral-800 to-meradio-dark
      backdrop-blur-lg border-t border-white border-opacity-10
      shadow-2xl
      ${className}
    `}>
      <div className="md:hidden">
        <div className="px-4 pt-2 pb-[env(safe-area-inset-bottom)] space-y-2">
          <ProgressBar
            showTime={false}
            height="thin"
            className="w-full"
          />

          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              <VinylRecord size="small" showLabel={false} />
              <TrackInfo
                size="small"
                showThumbnail={false}
                showAudioFeatures={false}
                className="flex-1 min-w-0"
              />
            </div>

            <PlayerControls
              size="small"
              showVolumeControl={false}
              showSkipControls={true}
              showModeControls={false}
            />
          </div>
        </div>
      </div>
      
      <div className="hidden md:block">
        <div className="px-6 py-4">
          <div className="grid grid-cols-[minmax(0,24rem)_1fr_minmax(0,24rem)] items-center gap-x-6">
            <div className="flex items-center space-x-4 min-w-0">
              <VinylRecord size="medium" showLabel={false} />
              <TrackInfo
                size="medium"
                showThumbnail={true}
                showAudioFeatures={true}
                className="flex-1 min-w-0"
              />
            </div>

            <div className="flex justify-center">
              <PlayerControls
                size="medium"
                showVolumeControl={false}
                showSkipControls={true}
                showModeControls={true}
              />
            </div>

            <div className="flex items-center justify-end space-x-2">
              <LikeButton
                videoId={currentVideo.id}
                initialLiked={likedVideos.includes(currentVideo.id)}
                onLikeChange={liked => dispatch(toggleVideoLike({ videoId: currentVideo.id, liked }))}
                size="medium"
              />
              <PlayerControls
                size="small"
                showVolumeControl={true}
                showSkipControls={false}
                showModeControls={false}
                showPlayButton={false}
              />
            </div>

            <div className="col-start-2 row-start-2 flex justify-center mt-2">
              <ProgressBar
                showTime={true}
                height="medium"
                className="w-full max-w-md"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-meradio-primary to-transparent opacity-50" />

      <style>{`
        .global-player {
          backdrop-filter: blur(15px);
          -webkit-backdrop-filter: blur(15px);

          background: rgba(45, 52, 54, 0.95);

          animation: slide-up 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        @supports (backdrop-filter: blur(15px)) or (-webkit-backdrop-filter: blur(15px)) {
          .global-player {
            background: linear-gradient(135deg, 
              rgba(45, 52, 54, 0.9) 0%, 
              rgba(99, 110, 114, 0.9) 50%,
              rgba(45, 52, 54, 0.9) 100%
            );
          }
        }
        
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @media (max-width: 768px) {
          .global-player {
            min-height: 72px;
          }
        }

        @media (min-width: 769px) {
          .global-player {
            min-height: 90px;
          }
        }

        .global-player {
          bottom: 0;
          position: fixed;
          overflow: visible;
        }
      `}</style>
    </div>
  );
};

export default GlobalPlayer;
